import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search, ArrowRight, MapPin } from 'lucide-react';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * US ZIP-code search — the US mirror of `UkPostcodeSearchBox`.
 *
 * Accepts a 3-5 digit ZIP (or ZIP+4), normalises it, and routes to
 * `/us/zip/<ZIP>`. Autocomplete fetches `/api/us/sold-prices/zips` for
 * suggestions ordered by transaction count. Same keyboard-nav + a11y
 * treatment as the UK box so the two feel like siblings.
 */
const UsZipSearchBox = ({ variant = 'hero' }) => {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);
  const [value, setValue] = useState('');
  const [error, setError] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const isHero = variant === 'hero';

  const normalise = (raw) => {
    const cleaned = (raw || '').trim().replace(/\D/g, '').slice(0, 5);
    if (cleaned.length < 3) return null;
    return cleaned;
  };

  const goToZip = (zip) => {
    if (!zip) return;
    setShowDropdown(false);
    navigate(`/us/zip/${zip}`);
  };

  // Debounced suggestion fetch. ≥1 char prefix.
  const fetchSuggestions = useCallback((prefix) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!prefix || prefix.length < 1) {
      setSuggestions([]);
      return;
    }
    debounceRef.current = setTimeout(() => {
      axios
        .get(`${BACKEND_URL}/api/us/sold-prices/zips`, { params: { prefix, limit: 6 } })
        .then((r) => {
          const rows = (r.data?.results || []).filter((s) => s.zip);
          setSuggestions(rows);
          setActiveIndex(-1);
        })
        .catch(() => setSuggestions([]));
    }, 180);
  }, []);

  const onChange = (e) => {
    const raw = e.target.value;
    setValue(raw);
    if (error) setError('');
    const digits = raw.replace(/\D/g, '').slice(0, 5);
    fetchSuggestions(digits);
    setShowDropdown(true);
  };

  const handleSubmit = (e) => {
    e?.preventDefault?.();
    if (activeIndex >= 0 && suggestions[activeIndex]) {
      goToZip(suggestions[activeIndex].zip);
      return;
    }
    const zip = normalise(value);
    if (!zip) {
      setError('Enter a valid US ZIP (e.g. 10001 or 90210)');
      inputRef.current?.focus();
      return;
    }
    setError('');
    goToZip(zip);
  };

  const onKeyDown = (e) => {
    if (!showDropdown || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, -1));
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
      setActiveIndex(-1);
    }
  };

  useEffect(() => {
    const onDocClick = (e) => {
      if (!wrapperRef.current?.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  return (
    <form
      ref={wrapperRef}
      onSubmit={handleSubmit}
      data-testid="us-zip-search-box"
      className={`relative flex items-stretch gap-2 max-w-xl ${isHero ? 'mt-8' : 'mt-4'}`}
    >
      <div
        className={`relative flex-1 flex items-center gap-2 rounded-full px-5 py-3 ${
          isHero
            ? 'bg-white/10 backdrop-blur ring-1 ring-white/20 focus-within:ring-white/40'
            : 'bg-white ring-1 ring-gray-200 focus-within:ring-[#7042cc]'
        }`}
      >
        <Search className={`h-4 w-4 shrink-0 ${isHero ? 'text-white/60' : 'text-gray-400'}`} />
        <input
          ref={inputRef}
          type="text"
          inputMode="numeric"
          autoComplete="postal-code"
          spellCheck={false}
          maxLength={5}
          value={value}
          onChange={onChange}
          onKeyDown={onKeyDown}
          onFocus={() => value && setShowDropdown(true)}
          placeholder="Type any US ZIP — e.g. 10001 or 90210"
          aria-label="US ZIP code"
          aria-autocomplete="list"
          aria-controls="us-zip-suggestions"
          aria-expanded={showDropdown && suggestions.length > 0}
          data-testid="us-zip-input"
          className={`flex-1 bg-transparent border-0 outline-none text-base tabular-nums placeholder:tracking-normal placeholder:text-sm ${
            isHero ? 'text-white placeholder:text-white/40' : 'text-gray-900 placeholder:text-gray-400'
          }`}
        />
      </div>
      <Button
        type="submit"
        data-testid="us-zip-submit"
        className={`rounded-full px-5 sm:px-6 font-semibold ${
          isHero
            ? 'bg-white text-[#7042cc] hover:bg-[#fff7eb]'
            : 'bg-[#7042cc] text-white hover:bg-[#5b35a8]'
        }`}
      >
        <span className="hidden sm:inline mr-2">See sold prices</span>
        <ArrowRight className="h-4 w-4" />
      </Button>

      {/* Suggestions dropdown */}
      {showDropdown && suggestions.length > 0 && (
        <ul
          id="us-zip-suggestions"
          role="listbox"
          data-testid="us-zip-suggestions"
          className="absolute left-0 right-[110px] top-full mt-2 z-30 overflow-hidden rounded-xl bg-white text-gray-900 ring-1 ring-gray-200 shadow-lg max-h-72 overflow-y-auto"
        >
          {suggestions.map((s, i) => {
            const active = i === activeIndex;
            return (
              <li
                key={s.zip}
                role="option"
                aria-selected={active}
                data-testid={`us-zip-suggestion-${s.zip}`}
                onMouseEnter={() => setActiveIndex(i)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  goToZip(s.zip);
                }}
                className={`flex items-center justify-between gap-3 px-4 py-2.5 cursor-pointer text-sm ${
                  active ? 'bg-[#7042cc]/10' : 'hover:bg-gray-50'
                }`}
              >
                <span className="inline-flex items-center gap-2 min-w-0">
                  <MapPin className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                  <span className="font-mono font-bold tabular-nums">{s.zip}</span>
                  {(s.city || s.state) && (
                    <span className="text-[11px] text-gray-500 truncate">
                      · {[s.city, s.state].filter(Boolean).join(', ')}
                    </span>
                  )}
                </span>
                <span className="text-[11px] text-gray-500 tabular-nums shrink-0">
                  {s.count} {s.count === 1 ? 'sale' : 'sales'}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {error && (
        <p
          role="alert"
          data-testid="us-zip-error"
          className={`absolute mt-14 text-xs ${isHero ? 'text-[#ffb37a]' : 'text-red-600'}`}
        >
          {error}
        </p>
      )}
    </form>
  );
};

export default UsZipSearchBox;
